import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { localizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { env } from '@/config/env';
import { EVENT_TYPE_FILTERS } from '@/features/events/event-enums';
import { useBookingServiceTypes } from '@/features/manage-bookings/bookings.api';
import { formatCurrency, formatPhone } from '@/lib/format';
import { toApiDate as apiDate } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import {
  usePaymentHistory,
  type PaymentHistoryModel,
  type PaymentHistoryRow,
} from './payment-history.api';

/**
 * Booking payment history — one row per payment taken for a booking.
 *
 * Two changes from the report this replaces, both about reaching the same
 * information:
 *
 * 1. The name column hid the email and phone behind a hover popover, which a
 *    touch screen has no way to open — the mobile card had to show them
 *    outright, so the two views disagreed about what the row contained. They
 *    are simply shown here.
 * 2. A timeslot was three stacked lines per end, six in one cell. It is one
 *    range on one line, the way every other page states a period.
 */

const ANY = 'ANY';

/**
 * `yyyy-MM-dd` in, wall-clock out — or null, which leaves that end of the range
 * open. Both dates are optional, so the report still defaults to everything.
 */
function toApiDate(value: string, endOfDay: boolean): string | null {
  return value ? apiDate(value, endOfDay) : null;
}

/**
 * These timestamps are wall-clock at the venue, so they are shifted forward by
 * the configured offset and then formatted in UTC — reading them as local time
 * would move every class by the browser's own offset.
 */
function venueTime(value: string | null | undefined, withTime = true) {
  if (!value) return '—';
  const date = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return '—';
  const shifted = new Date(date.getTime() + env.timezoneOffset * 3_600_000);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    timeZone: 'UTC',
  }).format(shifted);
}

/** Start and end as one range, collapsing the day when both fall on it. */
function slotRange(start: string | null, end: string | null) {
  const left = venueTime(start);
  const right = venueTime(end);
  if (left === '—' || right === '—') return left === '—' ? right : left;
  // "Fri, Mar 7, 6:00 PM – 7:30 PM" rather than repeating the date.
  const day = venueTime(start, false);
  return right.startsWith(day)
    ? `${left} – ${right.slice(day.length + 2)}`
    : `${left} – ${right}`;
}

function personName(row: PaymentHistoryRow) {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';
}

export function BookingPaymentHistoryPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [eventType, setEventType] = useState(ANY);
  const [serviceTypeId, setServiceTypeId] = useState(ANY);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const { data: serviceTypes } = useBookingServiceTypes();

  const model: PaymentHistoryModel = useMemo(
    () => ({
      searchTerm: query,
      startDate: toApiDate(start, false),
      endDate: toApiDate(end, true),
      eventType: eventType === ANY ? null : Number(eventType),
      serviceTypeId: serviceTypeId === ANY ? null : serviceTypeId,
      page,
      size,
    }),
    [query, start, end, eventType, serviceTypeId, page, size]
  );

  const history = usePaymentHistory(model);
  const rows = history.data?.payments ?? [];
  const total = history.data?.total ?? 0;

  const reset =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(0);
    };

  const filterFields: FilterField[] = [
    // Empty means "no bound", which is also the reset value — so an unset date
    // does not count towards the Filters badge on a phone.
    {
      id: 'start',
      label: t('bookingPaymentHistory.from'),
      value: start,
      onChange: reset(setStart),
      type: 'date',
      resetValue: '',
    },
    {
      id: 'end',
      label: t('bookingPaymentHistory.to'),
      value: end,
      onChange: reset(setEnd),
      type: 'date',
      resetValue: '',
    },
    {
      id: 'eventType',
      label: t('bookingPaymentHistory.eventType'),
      value: eventType,
      onChange: reset(setEventType),
      sort: false,
      options: [
        { value: ANY, label: t('bookingPaymentHistory.any') },
        ...EVENT_TYPE_FILTERS.map((type) => ({
          value: String(type.value),
          label: t(type.labelKey),
        })),
      ],
    },
    {
      id: 'serviceType',
      label: t('bookingPaymentHistory.serviceType'),
      value: serviceTypeId,
      onChange: reset(setServiceTypeId),
      options: [
        { value: ANY, label: t('bookingPaymentHistory.any') },
        ...(serviceTypes ?? []).map((type) => ({
          value: type.id,
          label: localizedName(type, language),
        })),
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('bookingPaymentHistory.title')}
        description={t('bookingPaymentHistory.subtitle')}
      />

      <FilterBar
        busy={history.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void history.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('bookingPaymentHistory.searchPlaceholder')}
                aria-label={t('bookingPaymentHistory.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          history.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('bookingPaymentHistory.showingOf', {
                shown: rows.length,
                total,
              })}
            </p>
          ) : null
        }
      />

      {history.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('bookingPaymentHistory.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void history.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {history.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2 p-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
          ))}
        </div>
      )}

      {history.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={history.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('bookingPaymentHistory.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={history.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('bookingPaymentHistory.name')}</TableHead>
                  <TableHead>{t('bookingPaymentHistory.event')}</TableHead>
                  <TableHead>{t('bookingPaymentHistory.timeSlot')}</TableHead>
                  <TableHead>{t('bookingPaymentHistory.paidOn')}</TableHead>
                  <TableHead className="text-right">
                    {t('bookingPaymentHistory.amount')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <p className="font-medium">{personName(row)}</p>
                      <p className="text-muted-foreground text-xs">
                        {row.email}
                      </p>
                      {row.phoneNumber && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatPhone(row.phoneNumber)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p>{row.eventName}</p>
                      {/* Cancelled is the row's own state, not a column of
                          yes/no that is "no" on almost every line. */}
                      {row.cancelled && (
                        <Badge variant="destructive" className="mt-0.5">
                          {t('bookingPaymentHistory.cancelled')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {slotRange(row.startTime, row.endTime)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {venueTime(row.paidOn)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(row.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={history.isFetching} />
            <div
              className={cn(
                'space-y-2',
                history.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-2 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{personName(row)}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {row.email}
                      </p>
                      {row.phoneNumber && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatPhone(row.phoneNumber)}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 font-semibold tabular-nums">
                      {formatCurrency(row.amount)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm">{row.eventName}</p>
                    {row.cancelled && (
                      <Badge variant="destructive" className="mt-0.5">
                        {t('bookingPaymentHistory.cancelled')}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-muted-foreground text-xs">
                        {t('bookingPaymentHistory.timeSlot')}
                      </p>
                      <p className="text-xs">
                        {slotRange(row.startTime, row.endTime)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        {t('bookingPaymentHistory.paidOn')}
                      </p>
                      <p className="text-xs">{venueTime(row.paidOn)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
}
